import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { SaasError } from '../lib/saas-client';
import {
  rpcApi,
  type MintedKey,
  type RpcKey,
  type RpcSummary,
} from '../lib/rpc-client';
import { formatDate, usd } from '../lib/money';
import { AddCard, asksForCard } from './AddCard';
import { PlanStats } from './PlanStats';

interface AccountCtx {
  summary: RpcSummary | null;
  summaryError: string | null;
  reloadSummary: () => Promise<void>;
  keys: RpcKey[] | null;
  keysError: string | null;
  reloadKeys: () => Promise<void>;
  /** The newest key created or rotated in this tab. Held in memory only: the
   *  quick start uses it, and it's gone on reload (keys are shown once). */
  lastMinted: MintedKey | null;
  setLastMinted: (k: MintedKey | null) => void;
}

const Ctx = createContext<AccountCtx | null>(null);

export function useAccount(): AccountCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAccount must be used inside AccountLayout');
  return ctx;
}

function message(e: unknown, fallback: string): string {
  return e instanceof SaasError ? e.message : fallback;
}

/** Page titles; the header nav names the same sections. */
const TITLES: Record<string, string> = {
  '/account': 'Overview',
  '/keys': 'API keys',
  '/usage': 'Usage',
  '/billing': 'Billing',
};

/** What the RPC does with this account's keys right now, in words. */
function StateBanner({ summary, onBilling }: { summary: RpcSummary; onBilling: boolean }) {
  const { state, billing, plan, period } = summary;
  if (state === 'suspended') {
    const why =
      billing.suspended_reason === 'payment_failed'
        ? 'a payment failed and the grace period ended'
        : billing.suspended_reason ?? 'the account is suspended';
    return (
      <div className="rpc-notice rpc-notice--error" role="alert" data-testid="state-banner">
        <strong>Your keys are paused.</strong> The RPC answers them with HTTP 401 because{' '}
        {why}.{' '}
        {billing.suspended_reason === 'payment_failed' ? (
          <Link to="/billing">Update your card in billing</Link>
        ) : (
          <>
            {/* [PAUL: support address. support@willow.tech doesn't exist yet.] */}
            <a href="mailto:info@willow.tech">Contact us</a>
          </>
        )}
        .
      </div>
    );
  }
  if (billing.payment_failed_at && plan.postpaid && !billing.grace_ends_at) {
    // A saas from before postpaid plans were paused for a failed payment
    // sends no grace end: their keys keep working.
    return (
      <div className="rpc-notice rpc-notice--warn" role="status" data-testid="state-banner">
        <strong>A payment failed.</strong> Your keys keep working. Pay the open invoice
        under Cards and invoices
        {!onBilling && (
          <>
            {' '}
            in <Link to="/billing">billing</Link>
          </>
        )}
        .
      </div>
    );
  }
  if (billing.payment_failed_at) {
    return (
      <div className="rpc-notice rpc-notice--warn" role="status" data-testid="state-banner">
        <strong>A payment failed.</strong> Update your card before{' '}
        {formatDate(billing.grace_ends_at)}, or your keys pause.{' '}
        {!onBilling && (
          <>
            <Link to="/billing">Go to billing</Link>.
          </>
        )}
      </div>
    );
  }
  if (state === 'exhausted') {
    const free = plan.unit_price_micros_per_million == null;
    return (
      <div className="rpc-notice rpc-notice--warn" role="status" data-testid="state-banner">
        <strong>Requests are getting HTTP 402.</strong>{' '}
        {free
          ? `This month's ${summary.units.included.toLocaleString('en-US')} free units are used up. Add credit to keep going, or wait until ${formatDate(period.end)}.`
          : `This period's units and your balance (${usd(summary.balance.cash_micros)}) are used up, or your spend cap is reached. Add credit or raise the cap to keep going.`}{' '}
        {!onBilling && (
          <>
            <Link to="/billing">Go to billing</Link>.
          </>
        )}
      </div>
    );
  }
  return null;
}

/** Shell for the signed-in RPC pages (/account, /keys, /usage, /billing):
 *  auth guard, account state, and shared data. Section nav and sign-out are in the header. */
export function AccountLayout() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [summary, setSummary] = useState<RpcSummary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [keys, setKeys] = useState<RpcKey[] | null>(null);
  const [keysError, setKeysError] = useState<string | null>(null);
  const [lastMinted, setLastMinted] = useState<MintedKey | null>(null);

  const reloadSummary = useCallback(async () => {
    try {
      setSummary(await rpcApi.summary());
      setSummaryError(null);
    } catch (e) {
      setSummaryError(message(e, 'Could not load your account.'));
    }
  }, []);

  const reloadKeys = useCallback(async () => {
    try {
      setKeys(await rpcApi.listKeys());
      setKeysError(null);
    } catch (e) {
      setKeysError(message(e, 'Could not load your keys.'));
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    reloadSummary();
    reloadKeys();
  }, [user, reloadSummary, reloadKeys]);

  const value = useMemo<AccountCtx>(
    () => ({
      summary,
      summaryError,
      reloadSummary,
      keys,
      keysError,
      reloadKeys,
      lastMinted,
      setLastMinted,
    }),
    [summary, summaryError, reloadSummary, keys, keysError, reloadKeys, lastMinted],
  );

  if (!loading && !user) {
    const back = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?return=${back}`} replace />;
  }

  return (
    <Ctx.Provider value={value}>
      <div className="rpc-page">
        <header className="rpc-page-header">
          {/* [PAUL: product name. The Oku docs say "Willow Log RPC".] */}
          <div className="panel-eyebrow">Willow RPC</div>
          <h1>{TITLES[location.pathname] ?? 'Account'}</h1>
        </header>
        {loading ? (
          // Until sign-in is confirmed: the frame, and on the overview its
          // tiles with placeholders, so nothing moves when the numbers come.
          location.pathname === '/account' && <PlanStats s={null} />
        ) : (
          <>
            {summaryError && (
              <div className="rpc-notice rpc-notice--error" role="alert">
                {summaryError}
              </div>
            )}
            {summary && (
              <StateBanner summary={summary} onBilling={location.pathname === '/billing'} />
            )}
            {summary &&
              location.pathname !== '/billing' &&
              asksForCard(summary, new URLSearchParams(location.search).get('rpc') === 'card_saved') && (
                <AddCard s={summary} />
              )}
            <Outlet />
          </>
        )}
      </div>
    </Ctx.Provider>
  );
}
